import type { EditorView } from "@tiptap/pm/view";

import type { MentionOption } from "./mention-options-list";
import { MENTION_CHAR, MENTION_NODE_NAME } from "./mention-node";

/** `@` followed by a word, unless a letter or digit comes before it, as in an email address. */
const AT_WORD = /(?<![\p{L}\p{N}])@([^\s@]+)/gu;

/** What may follow a name, so `@Ann` does not match inside `@Annie`. */
const NAME_END = /^$|^[\s.,;:!?)\]'"]/;

/**
 * The option the text after an `@` names: the longest label it starts with.
 * Null when nothing matches, or when two options share that label, since
 * guessing could notify the wrong person.
 */
function matchName(
  textAfterAt: string,
  options: MentionOption[],
): MentionOption | null {
  const matches = options.filter(
    (option) =>
      option.label.length > 0 &&
      textAfterAt.toLowerCase().startsWith(option.label.toLowerCase()) &&
      NAME_END.test(textAfterAt.slice(option.label.length)),
  );
  const longest = Math.max(0, ...matches.map((option) => option.label.length));
  const best = matches.filter((option) => option.label.length === longest);

  return best.length === 1 ? (best[0] ?? null) : null;
}

/**
 * Turns each `@Name` in a pasted range into a mention, as if it was picked
 * from the popup. Pasted text has names, not ids, so each name goes through
 * the popup's search. If the document changes during the search, the paste
 * stays as text.
 */
export async function linkPastedMentions(
  view: EditorView,
  { from, to }: { from: number; to: number },
  search: (query: string) => Promise<MentionOption[]>,
): Promise<void> {
  const doc = view.state.doc;
  const found: { pos: number; textAfterAt: string; query: string }[] = [];

  doc.nodesBetween(from, to, (node, pos) => {
    for (const match of (node.text ?? "").matchAll(AT_WORD)) {
      found.push({
        pos: pos + match.index,
        textAfterAt: (node.text ?? "").slice(match.index + 1),
        query: match[1] ?? "",
      });
    }
  });

  const resolved = await Promise.all(
    found.map(async ({ pos, textAfterAt, query }) => ({
      pos,
      option: matchName(textAfterAt, await search(query)),
    })),
  );
  const mentionType = view.state.schema.nodes[MENTION_NODE_NAME];

  if (view.isDestroyed || view.state.doc !== doc || !mentionType) return;

  const tr = view.state.tr;
  // Last first, so a replacement doesn't shift the positions before it.
  for (const { pos, option } of resolved.reverse()) {
    if (!option) continue;
    tr.replaceWith(
      pos,
      pos + MENTION_CHAR.length + option.label.length,
      mentionType.create({
        id: option.value,
        kind: option.kind,
        label: option.label,
      }),
    );
  }
  if (tr.docChanged) view.dispatch(tr);
}
